// apps/web/src/pages/coletor/fila-page.tsx — Fase 02, Plano 08 (OPS-09, colApeIni).
import { useState, useEffect, type ReactElement } from 'react';
import { FilaLocalBadge } from '../../components/coletor/fila-local';
import { ColetorBarraIdentidade } from '../../components/coletor/coletor-identidade';
import { montarUrlApontamento } from '../../lib/coletor-url';
import type { OsFilaColetor } from '@pieltec/contracts';
import { RevisaoFuturaDialog } from '../../components/desenhos/revisao-futura-dialog';
import { SolicitarMaterialDialog } from '../../components/coletor/solicitar-material-dialog';
import { SeletorCelulaColetor } from '../../components/coletor/seletor-celula';
import { BotaoSolicitarMaterialSetor, referenciaLoteOp, type PedidoMaterialTablet } from '../../components/coletor/solicitar-material-acao';
import { obterSessaoAtual } from '../../lib/session';
import {
  carregarOpcoesCelulas,
  dependenciasCelulasColetor,
  podeSolicitarMaterial,
  type OpcoesCelulaColetor,
} from '../../lib/coletor/celulas-coletor';

const SEM_OPCOES: OpcoesCelulaColetor = { vinculadas: [], outras: [], padrao: null };

export function FilaPage(): ReactElement {
  const [opcoes, setOpcoes] = useState<OpcoesCelulaColetor>(SEM_OPCOES);
  const [celulaId, setCelulaId] = useState<string | null>(null);
  const [fila, setFila] = useState<readonly OsFilaColetor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [loteDesenhoAberto, setLoteDesenhoAberto] = useState<string | null>(null);
  const [solicitacao, setSolicitacao] = useState<PedidoMaterialTablet | null>(null);
  const [podeSolicitar, setPodeSolicitar] = useState<boolean>(false);

  const fetchFila = async (celula: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/producao/fila/${encodeURIComponent(celula)}`);
      if (!res.ok) {
        throw new Error('Falha ao consultar fila de ordens de serviço da célula.');
      }
      const data = (await res.json()) as OsFilaColetor[];
      setFila(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao buscar fila');
    } finally {
      setLoading(false);
    }
  };

  // Células reais: as vinculadas a este tablet primeiro; sem vínculo, todas as ativas.
  useEffect(() => {
    let ativo = true;
    void obterSessaoAtual().then((sessao) => {
      if (ativo) setPodeSolicitar(podeSolicitarMaterial(sessao));
    });
    void carregarOpcoesCelulas(dependenciasCelulasColetor).then((carregadas) => {
      if (!ativo) return;
      setOpcoes(carregadas);
      setCelulaId((atual) => atual ?? carregadas.padrao);
      if (carregadas.padrao === null) setLoading(false);
    });
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    if (celulaId !== null) void fetchFila(celulaId);
  }, [celulaId]);

  const celulaAtual = [...opcoes.vinculadas, ...opcoes.outras].find((c) => c.codigo === celulaId) ?? null;

  const handleSelecionarOs = (os: OsFilaColetor) => {
    window.location.href = montarUrlApontamento({
      osId: os.osId,
      opNumero: String(os.opNumero),
      etapaOrdem: String(os.etapaOrdem),
      celulaId: os.celulaId,
    });
  };

  return (
    <div
      className="fila-page min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8 select-none"
      data-theme="dark-premium"
      data-density="touch"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        <ColetorBarraIdentidade />
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-300">
              Coletor
            </span>
            <h1 className="text-2xl font-bold text-white tracking-tight mt-0.5">
              Fila da célula
            </h1>
          </div>
          <FilaLocalBadge />
        </header>

        {/* Seletor de Célula / Posto */}
        <div className="flex items-center gap-3 bg-slate-900 p-4 rounded-xl border border-slate-800">
          <label htmlFor="coletor-celula" className="text-sm font-bold text-slate-300">Célula / Posto:</label>
          <SeletorCelulaColetor opcoes={opcoes} valor={celulaId} onAlterar={setCelulaId} />
          <button
            type="button"
            disabled={celulaId === null}
            onClick={() => { if (celulaId !== null) void fetchFila(celulaId); }}
            className="ml-auto min-h-[48px] px-3 py-2 bg-slate-800 hover:bg-slate-700 text-xs font-bold rounded-lg text-slate-300 disabled:opacity-50"
          >
            Atualizar
          </button>
        </div>

        {error && (
          <div className="p-4 bg-red-950/80 border border-red-500/50 text-red-200 rounded-xl text-xs" role="alert">
            {error}
          </div>
        )}

        {/* Lista de OSs Elegíveis */}
        <main className="space-y-3">
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-400">Consultando OSs elegíveis...</div>
          ) : celulaId === null ? (
            <div className="py-12 text-center text-sm text-slate-400 bg-slate-900/40 rounded-2xl border border-slate-800/80">
              Nenhuma célula ativa disponível para este tablet.
            </div>
          ) : fila.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400 bg-slate-900/40 rounded-2xl border border-slate-800/80">
              Nenhuma ordem de serviço aguardando nesta célula.
            </div>
          ) : (
            fila.map((os) => (
              <div
                key={os.osId}
                className="bg-slate-900 border border-slate-800 hover:border-emerald-500/60 p-5 rounded-2xl shadow-lg transition flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-emerald-700/30 border border-emerald-500/40 text-blue-300 text-xs font-mono font-bold rounded">
                      OP-{os.opNumero}
                    </span>
                    <span className="text-sm font-bold text-white">
                      Etapa #{os.etapaOrdem} — {os.celulaId}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 font-mono">
                    Lote: <span className="text-slate-200 font-bold">{os.loteCodigo}</span> · Item:{' '}
                    <span className="text-slate-300">{os.itemId}</span>
                  </div>
                  {os.etapaAnteriorOsId && (
                    <div className="text-[11px] text-emerald-400 font-mono">
                      ✓ Liberado por apontamento anterior ({os.qtdApontadaEtapaAnterior} un produzidas)
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setLoteDesenhoAberto(os.loteId)}
                    className="min-h-[56px] px-4 py-3 border border-emerald-400 text-emerald-100 font-bold text-sm rounded-xl touch-manipulation whitespace-nowrap"
                  >
                    Ver desenho
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelecionarOs(os)}
                    className="min-h-[56px] px-6 py-3 bg-emerald-700 hover:bg-emerald-600 active:bg-emerald-800 text-white font-bold text-sm rounded-xl shadow-lg uppercase tracking-wider touch-manipulation whitespace-nowrap"
                  >
                    Iniciar Apontamento
                  </button>
                  {podeSolicitar && <button type="button" onClick={() => setSolicitacao({ loteId: os.loteId, opId: os.opId, referencia: referenciaLoteOp(os.loteCodigo, os.opNumero) })} className="min-h-[56px] px-4 py-3 border border-emerald-400 text-emerald-100 font-bold text-sm rounded-xl touch-manipulation">
                    Solicitar material
                  </button>}
                </div>
              </div>
            ))
          )}
        </main>
      </div>
      <RevisaoFuturaDialog
        aberto={loteDesenhoAberto !== null}
        onAbertoChange={(aberto) => { if (!aberto) setLoteDesenhoAberto(null); }}
        loteId={loteDesenhoAberto ?? undefined}
      />
      {podeSolicitar && celulaAtual && (
        <div className="max-w-4xl mx-auto pb-8">
          <BotaoSolicitarMaterialSetor podeSolicitar celula={celulaAtual} rotulo="Solicitar consumível para este setor" onAbrir={setSolicitacao} />
        </div>
      )}
      {solicitacao && <SolicitarMaterialDialog aberto onAbertoChange={(aberto) => !aberto && setSolicitacao(null)} {...solicitacao} onSolicitado={() => { if (celulaId !== null) void fetchFila(celulaId); }} />}
    </div>
  );
}
